import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import { updateAppointmentsOrderApi, getSubscriptionCurrentApi } from "../services/apiService";
import elderlyCare from "../assets/images/Elderly-Care.svg";
import respiratorySupport from "../assets/images/Respiratory-Support.svg";
import wheelchairAccess from "../assets/images/Wheelchair-Access.svg";
import { isPreviousDateFromCurrent, useOnClickOutside } from "../utils/commonData";
import { useUserContext } from "../context/UserContext";
import AddAppointmentForm from "./AddAppointmentForm";
import AppointmentDetailsModal from "./AppointmentDetailsModal";

const StaticDayView = props => {
  const { userData } = useUserContext();
  const dropdownRef = useRef(null);

  // Sample data for time slots
  const [timeSlots, setTimeSlots] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [addAppPopupOpen, setAddAppPopupOpen] = useState(false);
  const [addAppInitialData, setAddAppInitialData] = useState({});
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [activeTab, setActiveTab] = useState("observations");

  const handleToggleSelect = index => {
    // Toggle the open index
    setOpenIndex(openIndex === index ? null : index);
  };

  const isTimeInRange = time => {
    const checkTime = moment(time, "HH:mm");
    return props?.intervalByDay.some(interval => {
      const start = moment(interval.startTime, "HH:mm");
      const end = moment(interval.endTime, "HH:mm");
      return checkTime.isBetween(start, end, null, "[]"); // '[]' includes boundaries
    });
  };

  const daysarray = [];
  for (let i = 1; i <= 31; i++) {
    daysarray.push(i < 10 ? "0" + i : i);
  }

  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 100;
  const years = [];

  for (let year = startYear; year <= currentYear; year++) {
    years.push(year);
  }

  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];



  const generateTimeSlots = responseHaveIntervals => {
    let timeSlots = [];
    let startTime = moment("00:00", "HH:mm");
    let endTime = moment("23:30", "HH:mm");

    // Loop through each 30-minute block
    while (startTime <= endTime) {
      // Format the time into HH:mm string
      let mainTime = startTime.format("HH:mm");
      let next15Min = moment(startTime).add(15, "minutes").format("HH:mm");

      // Initialize the slot object
      let slot = {
        mainTime: mainTime,
        subTimes: [
          { time: mainTime, status: "FREE" },
          { time: next15Min, status: "FREE" },
        ],
      };

      // Check if the current mainTime falls within any interval
      const interval = responseHaveIntervals.find(interval => {
        const intervalStart = moment(interval.startTime, "HH:mm");
        const intervalEnd = moment(interval.endTime, "HH:mm");
        return moment(mainTime, "HH:mm").isBetween(intervalStart, intervalEnd, null, "[]");
      });

      // If an interval is found, add intervalStartTime and intervalEndTime
      if (interval) {
        slot.intervalStartTime = interval.startTime;
        slot.intervalEndTime = interval.endTime;
        slot.intervalMaxVisitor = interval.visitor;
      }

      // Push the slot to timeSlots
      timeSlots.push(slot);

      // Move to the next 30-minute block
      startTime.add(30, "minutes");
    }

    // Add explicit interval start and end times not captured in the standard slots
    responseHaveIntervals.forEach(interval => {
      const intervalStart = moment(interval.startTime, "HH:mm").format("HH:mm");
      const intervalEnd = moment(interval.endTime, "HH:mm").format("HH:mm");

      // Ensure start time is in slots
      if (!timeSlots.find(slot => slot.mainTime === intervalStart)) {
        timeSlots.push({
          mainTime: intervalStart,
          subTimes: [{ time: intervalStart, status: "FREE" }],
          intervalStartTime: interval.startTime,
          intervalEndTime: interval.endTime,
          intervalMaxVisitor: interval.visitor,
        });
      }

      // Ensure end time is in slots if it's unique
      if (!timeSlots.find(slot => slot.mainTime === intervalEnd)) {
        timeSlots.push({
          mainTime: intervalEnd,
          subTimes: [{ time: intervalEnd, status: "FREE" }],
          intervalStartTime: interval.startTime,
          intervalEndTime: interval.endTime,
        });
      }
    });

    // Sort slots by time
    timeSlots.sort((a, b) => moment(a.mainTime, "HH:mm") - moment(b.mainTime, "HH:mm"));

    return timeSlots;
  };

  const updateTimeSlotsWithAppointments = (timeSlots, appointments, responseHaveIntervals) => {
    return timeSlots.map(slot => {
      // Check if the main time slot falls within any defined intervals
      const interval = responseHaveIntervals.find(interval => {
        const start = moment(interval.startTime, "HH:mm");
        const end = moment(interval.endTime, "HH:mm");

        const slotTime = moment(slot.mainTime, "HH:mm");

        return slotTime.isBetween(start, end, null, "[]");
      });
      if (interval) {
        // If there's an interval, check for appointments that fall within it
        const appointmentsInInterval = appointments.filter(appointment => {
          const appointmentTime = moment(appointment.time, "HH:mm");
          const startTime = moment(interval.startTime, "HH:mm"); // Convert startTime to moment
          const endTime = moment(interval.endTime, "HH:mm"); // Convert endTime to moment

          // Use moment objects for comparison
          return appointmentTime.isBetween(startTime, endTime, null, "[]");
        });

        // Map over the interval appointments and add them to the subTimes
        slot.subTimes = appointmentsInInterval.map(appointment => ({
          time: appointment.time,
          status: "Booked",
          id: appointment?.id,
          patient_id: appointment?.patient_id,
          doctor_id: appointment?.doctor_id,
          name: appointment?.title,
          type: appointment?.type ? appointment?.type : "free",
          questions: appointment?.questions,
          confirm: appointment?.confirm,
          appointmentStatus: appointment?.status ? appointment?.status : 0,
          orders: appointment?.orders,
          priority: appointment?.priority,
          supports: appointment?.supports,
          finish: appointment?.finish,
          refer_name: appointment?.refer_name,
          patient_details: appointment?.patient_details,
        }));

        return slot; // Return the updated slot
      }

      let hasExactAppointment = false;

      slot.subTimes = slot.subTimes.map(subSlot => {
        const appointment = appointments.find(app => app.time === subSlot.time);
        if (appointment) {
          hasExactAppointment = true;
          return {
            ...subSlot,
            status: "Booked",
            id: appointment?.id,
            patient_id: appointment?.patient_id,
            doctor_id: appointment?.doctor_id,
            name: appointment?.title,
            type: appointment?.type ? appointment?.type : "free",
            questions: appointment?.questions,
            confirm: appointment?.confirm,
            appointmentStatus: appointment?.status ? appointment?.status : 0,
            priority: appointment?.priority,
            supports: appointment?.supports,
            finish: appointment?.finish,
            refer_name: appointment?.refer_name,
            patient_details: appointment?.patient_details,
          };
        }
        return subSlot; // If no appointment found, return original subSlot
      });
      // console.log(slot)
      if (!hasExactAppointment) {
        // Check if there are any appointments in this slot's range but not in subTimes
        const slotStartTime = moment(slot.mainTime, "HH:mm");
        const slotEndTime = moment(slot.mainTime, "HH:mm").add(30, "minutes"); // Adjust end time of slot

        // Use "[)" to exclude slotEndTime from the range (so 14:00 won't fall into the 13:30 slot)
        appointments.forEach(appointment => {
          const appointmentTime = moment(appointment.time, "HH:mm");

          // Check if the appointment falls strictly between the start and the exclusive end of the current slot
          if (appointmentTime.isBetween(slotStartTime, slotEndTime, null, "[)")) {
            // Check if this appointment is already in subTimes
            const existsInSubTimes = slot.subTimes.some(subSlot => subSlot.time === appointment.time);
            if (!existsInSubTimes) {
              // If not, add a new subSlot for this appointment
              slot.subTimes.push({
                time: appointment.time,
                status: "Booked",
                id: appointment?.id,
                patient_id: appointment?.patient_id,
                doctor_id: appointment?.doctor_id,
                name: appointment?.title,
                type: appointment?.type ? appointment?.type : "free",
                questions: appointment?.questions,
                confirm: appointment?.confirm,
                appointmentStatus: appointment?.status ? appointment?.status : 0,
                priority: appointment?.priority,
                supports: appointment?.supports,
                finish: appointment?.finish,
                refer_name: appointment?.refer_name,
                patient_details: appointment?.patient_details,
              });

              // Sort the subTimes to keep the order
              slot.subTimes.sort((a, b) => moment(a.time, "HH:mm") - moment(b.time, "HH:mm"));
            }
          } else {
            const existsInSubTimes = slot.subTimes.some(subSlot => moment(subSlot.time).format("HH:mm") == moment(appointmentTime).format("HH:mm"));
            if (!existsInSubTimes) {
              // console.log(appointmentTime);
              const previousMainTime = timeSlots
                .map(slot => moment(slot.mainTime, "HH:mm"))
                .filter(slotTime => slotTime.isSameOrBefore(appointmentTime)) // Find all possible earlier slots
                .sort((a, b) => b.diff(a)) // Sort in descending order
                .shift();

              if (previousMainTime) {
                const previousSlot = timeSlots.find(slot => slot.mainTime === previousMainTime.format("HH:mm"));
                const existsInSubTimes = previousSlot.subTimes.some(subSlot => subSlot.time === appointment.time);
                if (!existsInSubTimes) {
                  previousSlot.subTimes.push({
                    time: appointment.time,
                    status: "Booked",
                    id: appointment?.id,
                    patient_id: appointment?.patient_id,
                    doctor_id: appointment?.doctor_id,
                    name: appointment?.title,
                    type: appointment?.type || "free",
                    questions: appointment?.questions,
                    confirm: appointment?.confirm,
                    appointmentStatus: appointment?.status || 0,
                    priority: appointment?.priority,
                    supports: appointment?.supports,
                    finish: appointment?.finish,
                    refer_name: appointment?.refer_name,
                    patient_details: appointment?.patient_details,
                  });
                }
              }

              // Sort the subTimes to keep the order
              slot.subTimes.sort((a, b) => moment(a.time, "HH:mm") - moment(b.time, "HH:mm"));
            }
          }
        });
      }
      return slot; // Return the updated slot
    });
  };

  // const updateTimeSlotsWithAppointments = (timeSlots, appointments, responseHaveIntervals) => {
  //   return timeSlots.map(slot => {
  //     // Check if the main time slot falls within any defined intervals
  //     const interval = responseHaveIntervals.find(interval => {
  //       const start = moment(interval.startTime, "HH:mm");
  //       const end = moment(interval.endTime, "HH:mm");
  //       const slotTime = moment(slot.mainTime, "HH:mm");

  //       return slotTime.isBetween(start, end, null, "[]");
  //     });

  //     if (interval) {
  //       // If there's an interval, check for appointments that fall within it
  //       const appointmentsInInterval = appointments.filter(appointment => {
  //         const appointmentTime = moment(appointment.time, "HH:mm");
  //         const startTime = moment(interval.startTime, "HH:mm");
  //         const endTime = moment(interval.endTime, "HH:mm");

  //         return appointmentTime.isBetween(startTime, endTime, null, "[]");
  //       });

  //       slot.subTimes = appointmentsInInterval.map(appointment => ({
  //         time: appointment.time,
  //         status: "Booked",
  //         id: appointment?.id,
  //         patient_id: appointment?.patient_id,
  //         doctor_id: appointment?.doctor_id,
  //         name: appointment?.title,
  //         type: appointment?.type ? appointment?.type : "free",
  //         questions: appointment?.questions,
  //         confirm: appointment?.confirm,
  //         appointmentStatus: appointment?.status ? appointment?.status : 0,
  //         orders: appointment?.orders,
  //         priority: appointment?.priority,
  //         supports: appointment?.supports,
  //         finish: appointment?.finish,
  //         refer_name: appointment?.refer_name,
  //         patient_details: appointment?.patient_details,
  //       }));

  //       return slot;
  //     }

  //     let hasExactAppointment = false;

  //     slot.subTimes = slot.subTimes.map(subSlot => {
  //       const appointment = appointments.find(app => app.time === subSlot.time);
  //       if (appointment) {
  //         hasExactAppointment = true;
  //         return {
  //           ...subSlot,
  //           status: "Booked",
  //           id: appointment?.id,
  //           patient_id: appointment?.patient_id,
  //           doctor_id: appointment?.doctor_id,
  //           name: appointment?.title,
  //           type: appointment?.type ? appointment?.type : "free",
  //           questions: appointment?.questions,
  //           confirm: appointment?.confirm,
  //           appointmentStatus: appointment?.status ? appointment?.status : 0,
  //           priority: appointment?.priority,
  //           supports: appointment?.supports,
  //           finish: appointment?.finish,
  //           refer_name: appointment?.refer_name,
  //           patient_details: appointment?.patient_details,
  //         };
  //       }
  //       return subSlot;
  //     });

  //     if (!hasExactAppointment) {
  //       const slotStartTime = moment(slot.mainTime, "HH:mm");
  //       const slotEndTime = moment(slot.mainTime, "HH:mm").add(30, "minutes");

  //       appointments.forEach(appointment => {
  //         const appointmentTime = moment(appointment.time, "HH:mm");

  //         if (appointmentTime.isBetween(slotStartTime, slotEndTime, null, "[)")) {
  //           const existsInSubTimes = slot.subTimes.some(subSlot => subSlot.time === appointment.time);

  //           if (!existsInSubTimes) {
  //             slot.subTimes.push({
  //               time: appointment.time,
  //               status: "Booked",
  //               id: appointment?.id,
  //               patient_id: appointment?.patient_id,
  //               doctor_id: appointment?.doctor_id,
  //               name: appointment?.title,
  //               type: appointment?.type ? appointment?.type : "free",
  //               questions: appointment?.questions,
  //               confirm: appointment?.confirm,
  //               appointmentStatus: appointment?.status ? appointment?.status : 0,
  //               priority: appointment?.priority,
  //               supports: appointment?.supports,
  //               finish: appointment?.finish,
  //               refer_name: appointment?.refer_name,
  //               patient_details: appointment?.patient_details,
  //             });
  //           }
  //         }
  //       });
  // console.log(slot)
  //       slot.subTimes.sort((a, b) => moment(a.time, "HH:mm") - moment(b.time, "HH:mm"));
  //     }

  //     return slot;
  //   });
  // };

  const handleDragStart = (index, subIndex) => {
    let fromItem = timeSlots[index]?.subTimes[subIndex];
    let tempObj = { appMainIndex: index, appSubIndex: subIndex };
    sessionStorage.setItem("doctolikAppointmentDragObject", JSON.stringify(tempObj));
  };

  const handleDragOver = event => {
    event.preventDefault();
  };

  const handleDrop = async (index, subIndex) => {
    const draggedItem = JSON.parse(sessionStorage.getItem("doctolikAppointmentDragObject"));
    if (!draggedItem || draggedItem.appMainIndex === undefined || draggedItem.appSubIndex === undefined) {
      return;
    }
    const fromMainIndex = draggedItem.appMainIndex;
    const fromSubIndex = draggedItem.appSubIndex;

    let fromItem = timeSlots[fromMainIndex]?.subTimes[fromSubIndex];
    let toItem = timeSlots[index]?.subTimes[subIndex];
    let currentDateInYMD = moment(props?.currentDate).format("yyyy-MM-DD");

    // if (toItem?.status !== "FREE" || moment(toItem?.time, "HH:mm").isBetween(moment("00:00", "HH:mm"), moment("05:00", "HH:mm"), null, "[]")) {
    //   toast.warning("Slot is not available.", {autoClose: 1500});
    //   return;
    // }

    // Swap the items
    // const updatedTimeSlots = [...timeSlots];

    // // Ensure valid indices
    // if (fromMainIndex !== index || fromSubIndex !== subIndex) {
    //   // Remove fromItem from its original position
    //   updatedTimeSlots[fromMainIndex].subTimes.splice(fromSubIndex, 1, toItem); // Replace fromItem with toItem
    //   updatedTimeSlots[index].subTimes.splice(subIndex, 1, fromItem); // Replace toItem with fromItem
    // }

    // setTimeSlots(updatedTimeSlots);
    if (fromItem?.id !== toItem?.id) {
      props?.handleUpdateAppointments(fromItem?.id, toItem?.id);
    }
    // try {
    //   const fd = new FormData();
    //   fd.append("from_id", fromItem?.id);
    //   fd.append("to_id", toItem?.id);
    //   let response = await updateAppointmentsOrderApi(fd);
    //   if (response) {
    //     if (response.data.res === true) {
    //       // setAbsenceReasonList(response.data.data);
    //     } else {
    //       toast.error(response.data.msg, {autoClose: 1500});
    //     }
    //   }
    // } catch (error) {
    //   console.error(error);
    //   toast.error("Something went wrong in server. Please try again.", {autoClose: 1500});
    // }

    sessionStorage.removeItem("doctolikAppointmentDragObject");
  };

  const scrollToFirstDiv = () => {
    const firstDiv = document.querySelector(".right-time-slots .cabinetDiv, .right-time-slots .videoDiv"); // Select first matching element within right-time-slots

    if (firstDiv) {
      firstDiv.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToCurrentTimeDiv = () => {
    let curTime;

    const now = new Date();

    // Format the time for Algeria's timezone (CET/CEST)
    const options = {
      timeZone: "Africa/Algiers",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
    const formatter = new Intl.DateTimeFormat("en-GB", options);
    // console.log(formatter.format(now));
    // setAlgeriaCurrentTime(formatter.format(now));
    // setAlgeriaCurrentTime("11:00");
    // curTime = "13:48";
    curTime = formatter.format(now);

    const currentHour = parseInt(curTime.split(":")[0], 10);
    const currentMinute = parseInt(curTime.split(":")[1], 10);
    const scrollToIndex = Math.floor(currentMinute / 30) + currentHour * 2;

    // Use document.querySelector to select the element
    const timeSlotElement = document.querySelector(`.time-slot:nth-of-type(${scrollToIndex + 1})`);

    if (timeSlotElement) {
      timeSlotElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleClickOutside = event => {
    // Check if the clicked element is outside the dropdown
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      // setOpenIndex(null); // Close the dropdown if clicked outside
    }
  };

  const handleAppointmentFormSuccess = (appointmentData, actionType) => {
    // Handle different action types from the form
    switch (actionType) {
      case 'slot':
      case 'without-tracking':
      case 'found':
        if (props?.handleAddAppointment) {
          appointmentData.date = props?.currentDate;
          appointmentData.patientCityId = appointmentData?.patientAddress;
          appointmentData.patientAddress = props?.cityList?.find(item => parseInt(item?.id) === parseInt(appointmentData.patientAddress))?.name || "";
          if (actionType === 'without-tracking') {
            appointmentData.appointmentType = 'without-tracking';
          } else if (actionType === 'slot') {
            appointmentData.appointmentType = 'slot';
          }
          props.handleAddAppointment(appointmentData);
        }
        break;
      case 'update-slot':
        if (props?.handleUpdateSlotBlock) {
          appointmentData.date = props?.currentDate;
          appointmentData.patientCityId = appointmentData?.patientAddress;
          appointmentData.patientAddress = props?.cityList?.find(item => parseInt(item?.id) === parseInt(appointmentData.patientAddress))?.name || "";
          props.handleUpdateSlotBlock(appointmentData, () => { });
        }
        break;
      case 'validate-profile-slot':
      case 'validate-profile':
        if (props?.handleUpdatePatientFromDoctorByPatientId) {
          props.handleUpdatePatientFromDoctorByPatientId(appointmentData, () => { });
        }
        break;
      default:
        if (props?.handleAddAppointment) {
          appointmentData.date = props?.currentDate;
          props.handleAddAppointment(appointmentData);
        }
    }
  };

  const handleOpenAppointmentModal = (appointment) => {
    setSelectedAppointment(appointment);
    setActiveTab(userData?.users?.type === "Nurse" ? "payments" : "observations");
    setShowAppointmentModal(true);
  };

  const handleCloseAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
    setActiveTab("observations");
  };

  useEffect(() => {
    // Add event listener to detect clicks outside
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Cleanup the event listener on component unmount
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Generate time slots
    let slots = generateTimeSlots(props?.intervalByDay);
    sessionStorage.removeItem("doctolikAddAppObjByDoc");
    sessionStorage.removeItem("doctolikAddAppObjByNurse");

    // Update the slots with the API response
    if (props?.mainEvents) {
      let updatedSlots = updateTimeSlotsWithAppointments(slots, props?.mainEvents, props?.intervalByDay);
      setTimeSlots(updatedSlots);
      setTimeout(() => {
        // scrollToFirstDiv();
        scrollToCurrentTimeDiv();
      }, 500);
    }

    // console.log(props?.mainEvents);
  }, [props?.screenLoading, props?.mainEvents]);

  useEffect(() => {
    if (props?.mainEvents) {
      setTimeout(() => {
        // scrollToFirstDiv();
      }, 500);
    }
  }, [props?.needToScroll]);



  return (
    <>
      <style>{`
        .nurseHdnNew {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          flex-wrap: nowrap;
          width: 100%;
        }

        .addNursePopup.addAppointment {
          position: relative;
        }

        .popup-content {
          position: relative;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-left: auto;
          flex-wrap: nowrap;
        }

        .scan-history-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
        }

        .closePopupBtn {
          position: absolute;
          margin-top: -30px;
          margin-right: -30px;
          top: 12px;
          right: 12px;
          width: 36px;
          height: 36px;
          border: 1px solid #e5e7eb;
          border-radius: 50%;
          background: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
          transition: box-shadow 0.2s ease;
        }

        .closePopupBtn:hover {
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
        }

        .closePopupBtn img {
          width: 16px;
          height: 16px;
        }
        
        .header-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-left: auto;
        }
        
        .mrzScannerBtn, .mrzHistoryBtn {
          padding: 8px 16px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }
        
        .mrzScannerBtn:hover, .mrzHistoryBtn:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }
        
        .mrzHistoryBtn {
          background: #10b981;
          margin-top: -10px;
        }
        
        .mrzHistoryBtn:hover {
          background: #059669;
        }
        
        .scan-history-wrapper {
          position: relative;
        }
        
        .scan-history-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.1);
          width: 400px;
          max-height: 500px;
          z-index: 1000;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        .history-header {
          padding: 16px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }
        
        .history-header h3 {
          margin: 0 0 12px 0;
          font-size: 16px;
          font-weight: 600;
          color: #111827;
        }
        
        .history-search {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
        }
        
        .history-search:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .history-list {
          overflow-y: auto;
          max-height: 400px;
          flex: 1;
        }
        
        .no-history {
          padding: 40px 20px;
          text-align: center;
          color: #9ca3af;
          font-size: 14px;
        }
        
        .history-item {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .history-item:hover {
          background: #f9fafb;
        }
        
        .history-item:last-child {
          border-bottom: none;
        }
        
        .history-item-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }
        
        .patient-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }
        
        .patient-name {
          font-weight: 600;
          color: #111827;
          font-size: 14px;
        }
        
        .patient-id {
          font-size: 12px;
          color: #6b7280;
        }
        
        .scan-date {
          font-size: 12px;
          color: #9ca3af;
          white-space: nowrap;
        }
        
        .load-more-btn {
          width: 100%;
          padding: 12px;
          background: #f3f4f6;
          border: none;
          border-top: 1px solid #e5e7eb;
          color: #374151;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .load-more-btn:hover {
          background: #e5e7eb;
        }
        
        .patient-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          margin-right: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .patient-avatar:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }
        
        .left-time-slots-sec {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        
        .confirmTag {
          flex-shrink: 0;
        }
        
        .mainSelectSpan {
          flex-shrink: 0;
        }
        
        .StylesCheckbox {
          flex-shrink: 0;
        }
        
        .infoBtn, .cancelBtn, .resceheduleBtn {
          flex-shrink: 0;
        }
        
        .selectSpanWrapper {
          display: flex;
          gap: 6px;
          flex-shrink: 0;
        }
        
        @media (max-width: 768px) {
          .nurseHdnNew {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .header-actions {
            width: 100%;
            flex-direction: column;
            margin-left: 0;
          }
          
          .mrzScannerBtn, .mrzHistoryBtn {
            width: 100%;
            justify-content: center;
          }
          
          .scan-history-dropdown {
            width: 100vw;
            max-width: 100%;
            left: 0;
            right: 0;
          }
        }
      `}</style>
      <div className="schedule-container">
        <div className="schedule-header">
          <div className="schedule-time-header">Time</div>
          <div className="schedule-status-header"></div>
        </div>
        <div className="schedule-body">
          {timeSlots.map((slot, index) => (

            <div className="time-slot" key={index}>
              {/* {console.log(moment(slot?.mainTime, "HH:mm").isBefore(moment("11:30", "HH:mm")))} */}
              <div
                className={`${!props?.isListView &&
                  moment(slot?.mainTime, "HH:mm").isAfter(moment("12:00", "HH:mm")) &&
                  moment(slot?.mainTime, "HH:mm").isBefore(moment("24:00", "HH:mm"))
                  ? "secondHalf"
                  : !props?.isListView && moment(slot?.mainTime, "HH:mm").isBefore(moment("12:00", "HH:mm"))
                    ? "firstHalf"
                    : "secondHalf"
                  } ${moment(slot?.mainTime, "HH:mm").isBetween(moment("00:00", "HH:mm"), moment("05:00", "HH:mm"), null, "[]") ? "timeDisablegrey" : ""}`}
                onClick={() => setOpenIndex(null)}>
                {isTimeInRange(slot.mainTime) ? (
                  <div className="time-interval-main-container">
                    <div
                      className={`time-interval-main  ${props?.intervalByDay.some(interval => interval.startTime === slot?.mainTime) ? "start-time" : ""} ${props?.intervalByDay.some(interval => interval.endTime === slot?.mainTime) ? "end-time" : ""
                        }`}>
                      <div
                        className={`left-time-slots`}
                      // className={`left-time-slots ${(() => {
                      //   const intervalStart = props?.intervalByDay.find(interval => interval.startTime === slot?.mainTime);
                      //   const intervalEnd = props?.intervalByDay.find(interval => interval.endTime === slot?.mainTime);
                      //   {console.log(intervalStart)}
                      //   {console.log(intervalEnd)}
                      //   if (intervalStart && intervalEnd) {
                      //     const startTime = moment(intervalStart.startTime, "HH:mm");
                      //     const endTime = moment(intervalEnd.endTime, "HH:mm"); // Make sure to access endTime correctly

                      //     return moment().isBetween(startTime, endTime, null, "[]") ? "current-time" : "";
                      //   }
                      //   return "";
                      // })()}`}
                      >
                        <div className="inner-slots">{slot?.mainTime}</div>
                        <span className="toClass">To</span>
                      </div>
                      {slot.subTimes.length > 0 ? (
                        <>
                          <div className="right-time-slots">
                            {slot.subTimes.map((subSlot, subIndex) => (
                              <React.Fragment key={subIndex}>
                                <div
                                  className={`events-slots ${subSlot?.appointmentStatus === 6
                                    ? "freeDiv slotBlocked"
                                    : subSlot.type === "cabinet"
                                      ? "cabinetDiv"
                                      : subSlot.type === "video"
                                        ? "videoDiv"
                                        : ""
                                    }`}
                                  key={subIndex}
                                  draggable={(subSlot.type === "cabinet" || subSlot.type === "video") && subSlot?.appointmentStatus !== 3 ? true : false}
                                  onDragStart={() => handleDragStart(index, subIndex)}
                                  onDragOver={handleDragOver}
                                  onDrop={() => handleDrop(index, subIndex)}>
                                  {/* <div className="events-slots">
                                  {subSlot.time} {subSlot.name}
                                </div> */}
                                  {subSlot.type === "cabinet" || subSlot.type === "video" ? (
                                    <div className={`left-time-span ${!props?.isListView ? "columnView" : ""}`}>
                                      <div
                                        className="slotName"
                                        onClick={() => {
                                          if (subSlot?.appointmentStatus === 6 && !props?.isPreviousDateFromCurrent) {
                                            setAddAppInitialData({
                                              patientId: subSlot?.patient_details[0]?.id || "",
                                              patientFirstName: subSlot?.patient_details[0]?.first_name || "",
                                              patientLastName: subSlot?.patient_details[0]?.last_name || "",
                                              patientGender: subSlot?.patient_details[0]?.gender || "",
                                              patientCity: subSlot?.patient_details[0]?.city_id || "",
                                              patientMonth: subSlot?.patient_details[0]?.dob ? subSlot?.patient_details[0]?.dob?.split("-")[1] : "",
                                              patientDay: subSlot?.patient_details[0]?.dob ? subSlot?.patient_details[0]?.dob?.split("-")[2] : "",
                                              patientYear: subSlot?.patient_details[0]?.dob ? subSlot?.patient_details[0]?.dob?.split("-")[0] : "",
                                              patientNumber: subSlot?.patient_details[0]?.cell_phone || "",
                                              idNumber: subSlot?.patient_details[0]?.id_number || "",
                                              birthCity: subSlot?.patient_details[0]?.birth_city_id || "",
                                              popupType: "slot",
                                              appointmentId: subSlot?.id,
                                            });
                                            setAddAppPopupOpen(true);
                                          } else {
                                            if (userData?.users?.type === "Doctor") {
                                              props?.navigate(`/doctor/appointment-details/${subSlot?.id}`);
                                            }
                                          }
                                        }}>
                                        {subSlot.name}
                                      </div>
                                      {subSlot?.priority === 1 ? <span className="priority-span">Priority</span> : null}
                                      {subSlot?.refer_name ? <span className="priority-span">Referred from Dr.{subSlot?.refer_name}</span> : null}
                                      {subSlot?.supports ? (
                                        <span className="priority-span support-span">
                                          {subSlot?.supports === "Respiratory Support" ? (
                                            <img src={respiratorySupport} />
                                          ) : subSlot?.supports === "Wheelchair Access" ? (
                                            <img src={wheelchairAccess} />
                                          ) : subSlot?.supports === "Elderly Care" ? (
                                            <img src={elderlyCare} />
                                          ) : (
                                            ""
                                          )}
                                          {subSlot?.supports}
                                        </span>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <div className={`left-time-span ${!props?.isListView ? "columnView" : ""}`}>
                                      {subSlot.name}
                                      {/* <span className="orange-priority-span">Priority</span> */}
                                    </div>
                                  )}
                                  <div className="box-content">
                                    {subSlot?.questions?.map((quesItem, quesIndex) =>
                                      quesItem?.priority === 1 ? (
                                        quesItem?.options_value && quesItem.options_value.length > 20 ? (
                                          <Tooltip title={`${quesItem?.label}: ${quesItem.options_value}`} placement="top-start">
                                            <span key={quesIndex} className="orange-priority-span">
                                              {quesItem?.label}: {quesItem.options_value.substring(0, 20)}...
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <span key={quesIndex} className="orange-priority-span">
                                            {quesItem?.label}: {quesItem.options_value}
                                          </span>
                                        )
                                      ) : quesItem?.options_value && quesItem.options_value.length > 20 ? (
                                        <Tooltip title={`${quesItem?.label}: ${quesItem.options_value}`} placement="top-start">
                                          <span key={quesIndex}>
                                            {quesItem?.label}: {quesItem.options_value.substring(0, 20)}...
                                          </span>
                                        </Tooltip>
                                      ) : (
                                        <span key={quesIndex}>
                                          {quesItem?.label}: {quesItem.options_value}
                                        </span>
                                      )
                                    )}
                                    {/* <span>Q1: Yes</span>
                                <span>Q2: pain in the head</span>
                                <span>Q3: BBBBBBBBS</span> */}
                                  </div>
                                  <div className="left-time-slots-sec" ref={dropdownRef}>
                                    {/* <div className="left-time-slots-sec"> */}
                                    {subSlot?.confirm === 1 && subSlot?.appointmentStatus !== 3 ? <span className="confirmTag">Confirmed</span> : null}
                                    {subSlot?.appointmentStatus === 3 ? <span className="confirmTag absentText">Absent</span> : null}
                                    {subSlot?.appointmentStatus === 2 ? <span className="confirmTag cancelText">Cancelled</span> : null}
                                    {subSlot.type ? (
                                      <>
                                        {subSlot?.appointmentStatus !== 3 && subSlot?.appointmentStatus !== 2 ? (
                                          <>
                                            {!isPreviousDateFromCurrent(props?.currentDate) ? (
                                              <span
                                                className={`mainSelectSpan ${subSlot.finish === 1 ? "green-span" : subSlot.finish === 2 ? "blue-span" : "gray-span"
                                                  } ${isPreviousDateFromCurrent(props?.currentDate) ? "disabled" : ""}`}
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  handleToggleSelect(index + subIndex);
                                                }}></span>
                                            ) : (
                                              <span
                                                className={`mainSelectSpan ${subSlot.finish === 1 ? "green-span" : subSlot.finish === 2 ? "blue-span" : "gray-span"
                                                  } ${isPreviousDateFromCurrent(props?.currentDate) ? "disabled" : ""}`}></span>
                                            )}

                                            {openIndex === subIndex + index && (
                                              <div className="selectSpanWrapper">
                                                <span
                                                  className="gray-span green-span"
                                                  onClick={event => {
                                                    event.stopPropagation();
                                                    props?.handleAppIsFinished(subSlot?.id, 1);
                                                    setOpenIndex(null);
                                                  }}></span>
                                                <span
                                                  className="gray-span blue-span"
                                                  onClick={event => {
                                                    event.stopPropagation();
                                                    props?.handleAppIsFinished(subSlot?.id, 2);
                                                    setOpenIndex(null);
                                                  }}></span>
                                              </div>
                                            )}
                                          </>
                                        ) : null}

                                        {(subSlot?.appointmentStatus === 0 || subSlot?.appointmentStatus === 1 || subSlot?.appointmentStatus === 4) &&
                                          subSlot.finish === 0 &&
                                          !isPreviousDateFromCurrent(props?.currentDate) ? (
                                          <div
                                            className="StylesCheckbox"
                                            onClick={() =>
                                              props?.selectedCheckboxes.has(parseInt(subSlot.id))
                                                ? props?.handleCheckboxChange({ id: parseInt(subSlot?.id), target: { checked: false } })
                                                : props?.handleCheckboxChange({ id: parseInt(subSlot?.id), target: { checked: true } })
                                            }>
                                            <input type="checkbox" value={parseInt(subSlot.id)} checked={props?.selectedCheckboxes.has(parseInt(subSlot.id))} />
                                            <div className="checkmark"></div>
                                          </div>
                                        ) : null}


                                        {userData?.users?.type === "Doctor" ? (
                                          <>
                                            <button
                                              className="infoBtn"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenAppointmentModal(subSlot);
                                              }}
                                              title="View Appointment Details">
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                              </svg>
                                            </button>
                                            <button
                                              className="cancelBtn"
                                              onClick={() => {
                                                if (props?.handleCancelBtnClick) {
                                                  props?.handleCancelBtnClick(subSlot?.id);
                                                }
                                              }}>
                                              Cancel
                                            </button>
                                            <button
                                              className="resceheduleBtn"
                                              onClick={() => {
                                                if (props?.handleRescheduleBtnClick) {
                                                  props?.handleRescheduleBtnClick(subSlot?.id, subSlot?.doctor_id, subSlot?.consultation_type);
                                                }
                                              }}>
                                              Reschedule
                                            </button>
                                          </>
                                        ) : null}
                                        {userData?.users?.type === "Nurse" ? (
                                          <>
                                            <button
                                              className="infoBtn"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenAppointmentModal(subSlot);
                                              }}
                                              title="View Payment Details">
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                              </svg>
                                            </button>
                                            <button
                                              className="cancelBtn"
                                              onClick={() => {
                                                if (props?.handleCancelBtnClick) {
                                                  props?.handleCancelBtnClick(subSlot?.id);
                                                }
                                              }}>
                                              Cancel
                                            </button>
                                          </>
                                        ) : null}
                                      </>
                                    ) : null}
                                  </div>
                                </div>
                              </React.Fragment>
                            ))}
                            {parseInt(slot?.intervalMaxVisitor) - parseInt(slot.subTimes.length) > 0 ? (
                              <div className={`events-slots freeDiv`}>
                                <div className={`left-time-span freeIntervalSlot ${!props?.isListView ? "columnView" : ""}`}>
                                  <div className="freeSlotCount">
                                    {parseInt(slot?.intervalMaxVisitor) - parseInt(slot.subTimes.length)} FREE SLOTS{" "}
                                    {!isPreviousDateFromCurrent(props?.currentDate) ? (
                                      <button
                                        className="addAppointmentBtn"
                                        onClick={async () => {
                                          // Check subscription status first
                                          try {
                                            const res = await getSubscriptionCurrentApi();
                                            if (!res.data?.success || !res.data?.data) {
                                              toast.warning("Attention, votre abonnement est terminé. Veuillez payer pour pouvoir créer une consultation.", { autoClose: 3000 });
                                              return;
                                            }
                                          } catch (error) {
                                            if (error.response?.status === 403) {
                                              toast.warning("Attention, votre abonnement est terminé. Veuillez payer pour pouvoir créer une consultation.", { autoClose: 3000 });
                                              return;
                                            }
                                          }

                                          if (slot?.intervalStartTime && slot?.intervalEndTime && !props?.isPreviousDateFromCurrent) {
                                            setAddAppInitialData({ appTime: `${slot?.intervalStartTime}-${slot?.intervalEndTime}` });
                                            if (!moment(slot?.mainTime, "HH:mm").isBetween(moment("00:00", "HH:mm"), moment("05:00", "HH:mm"), null, "[]")) {
                                              setAddAppPopupOpen(true);
                                            }
                                          }
                                        }}
                                      >
                                        Add Appointment
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="right-time-slots right-time-slots-blanks">
                            <div className={`events-slots freeDiv`}>
                              <div className={`left-time-span freeIntervalSlot ${!props?.isListView ? "columnView" : ""}`}>
                                <div className="freeSlotCount">
                                  {slot?.intervalMaxVisitor} FREE SLOTS{" "}
                                  {!isPreviousDateFromCurrent(props?.currentDate) ? (
                                    <button
                                      className="addAppointmentBtn"
                                      onClick={async () => {
                                        // Check subscription status first
                                        try {
                                          const res = await getSubscriptionCurrentApi();
                                          if (!res.data?.success || !res.data?.data) {
                                            toast.warning("Attention, votre abonnement est terminé. Veuillez payer pour pouvoir créer une consultation.", { autoClose: 3000 });
                                            return;
                                          }
                                        } catch (error) {
                                          if (error.response?.status === 403) {
                                            toast.warning("Attention, votre abonnement est terminé. Veuillez payer pour pouvoir créer une consultation.", { autoClose: 3000 });
                                            return;
                                          }
                                        }

                                        if (slot?.intervalStartTime && slot?.intervalEndTime && !props?.isPreviousDateFromCurrent) {
                                          setAddAppInitialData({ appTime: `${slot?.intervalStartTime}-${slot?.intervalEndTime}` });
                                          if (!moment(slot?.mainTime, "HH:mm").isBetween(moment("00:00", "HH:mm"), moment("05:00", "HH:mm"), null, "[]")) {
                                            setAddAppPopupOpen(true);
                                          }
                                        }
                                      }}
                                    >
                                      Add Appointment
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                              &nbsp;
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className={`time-slots-main ${props?.intervalByDay.some(interval => interval.startTime === slot?.mainTime) ? "start-time" : ""} ${props?.intervalByDay.some(interval => interval.endTime === slot?.mainTime) ? "end-time" : ""
                      }`}
                    onClick={e => {
                      e.stopPropagation();
                      setOpenIndex(null);
                    }}>
                    <div
                      className={`left-time-slots ${moment().isBetween(moment(slot?.mainTime, "HH:mm"), moment(slot?.mainTime, "HH:mm").add(30, "minutes"), null, "[]") ? "current-time" : ""
                        }`}>
                      {slot?.mainTime}
                    </div>
                    {slot.subTimes.length > 0 ? (
                      <>
                        <div className="right-time-slots">
                          {slot.subTimes.map((subSlot, subIndex) => (
                            <React.Fragment key={subIndex}>
                              <div
                                className={`events-slots ${subSlot?.appointmentStatus === 6
                                  ? "freeDiv slotBlocked"
                                  : subSlot.type === "cabinet"
                                    ? "cabinetDiv"
                                    : subSlot.type === "video"
                                      ? "videoDiv"
                                      : "freeDiv"
                                  }`}
                                key={subIndex}
                                onClick={() => {
                                  if (subSlot.type !== "cabinet" && subSlot.type !== "video" && !props?.isPreviousDateFromCurrent) {
                                    setAddAppInitialData({ appTime: subSlot.time });
                                    if (!moment(slot?.mainTime, "HH:mm").isBetween(moment("00:00", "HH:mm"), moment("05:00", "HH:mm"), null, "[]")) {
                                      setAddAppPopupOpen(true);
                                    }
                                  }
                                }}
                              >
                                {subSlot.type === "cabinet" || subSlot.type === "video" ? (
                                  <div className={`left-time-span ${!props?.isListView ? "columnView" : ""}`}>
                                    <div
                                      className="slotName"
                                      onClick={() => {
                                        if (subSlot?.appointmentStatus === 6 && !props?.isPreviousDateFromCurrent) {
                                          setAddAppInitialData({
                                            patientId: subSlot?.patient_details[0]?.id || "",
                                            patientFirstName: subSlot?.patient_details[0]?.first_name || "",
                                            patientLastName: subSlot?.patient_details[0]?.last_name || "",
                                            patientGender: subSlot?.patient_details[0]?.gender || "",
                                            patientCity: subSlot?.patient_details[0]?.city_id || "",
                                            patientMonth: subSlot?.patient_details[0]?.dob ? subSlot?.patient_details[0]?.dob?.split("-")[1] : "",
                                            patientDay: subSlot?.patient_details[0]?.dob ? subSlot?.patient_details[0]?.dob?.split("-")[2] : "",
                                            patientYear: subSlot?.patient_details[0]?.dob ? subSlot?.patient_details[0]?.dob?.split("-")[0] : "",
                                            patientNumber: subSlot?.patient_details[0]?.cell_phone || "",
                                            idNumber: subSlot?.patient_details[0]?.id_number || "",
                                            birthCity: subSlot?.patient_details[0]?.birth_city_id || "",
                                            popupType: "slot",
                                            appointmentId: subSlot?.id,
                                            appTime: subSlot.time,
                                          });

                                          setAddAppPopupOpen(true);
                                        } else {
                                          if (userData?.users?.type === "Doctor") {
                                            props?.navigate(`/doctor/appointment-details/${subSlot?.id}`);
                                          }
                                        }
                                      }}>
                                      {subSlot.time} {subSlot.name}
                                    </div>
                                    {subSlot?.priority === 1 ? <span className="priority-span">Priority</span> : null}
                                    {subSlot?.refer_name ? <span className="priority-span">Referred from Dr.{subSlot?.refer_name}</span> : null}
                                    {subSlot?.supports ? (
                                      <span className="priority-span support-span">
                                        {subSlot?.supports === "Respiratory Support" ? (
                                          <img src={respiratorySupport} />
                                        ) : subSlot?.supports === "Wheelchair Access" ? (
                                          <img src={wheelchairAccess} />
                                        ) : subSlot?.supports === "Elderly Care" ? (
                                          <img src={elderlyCare} />
                                        ) : (
                                          ""
                                        )}
                                        {subSlot?.supports}
                                      </span>
                                    ) : null}
                                  </div>
                                ) : (
                                  <div className={`left-time-span ${!props?.isListView ? "columnView" : ""}`}>
                                    {subSlot.time} {subSlot.name}
                                    {!moment(slot?.mainTime, "HH:mm").isBetween(moment("00:00", "HH:mm"), moment("05:00", "HH:mm"), null, "[]") ? (
                                      <></>
                                    ) : // <button
                                      //   className="addAppointmentBtn"
                                      //   // onClick={() => console.log(subSlot.time)}
                                      //   onClick={() => setAddAppPopupOpen(true)}>
                                      //   Add Appointment
                                      // </button>
                                      null}
                                    {/* <span className="priority-span">Priority</span>
                                  <span className="orange-priority-span">Priority</span> */}
                                  </div>
                                )}

                                <div className="box-content">
                                  {subSlot?.questions?.map((quesItem, quesIndex) =>
                                    quesItem?.priority === 1 ? (
                                      quesItem?.options_value && quesItem.options_value.length > 20 ? (
                                        <Tooltip key={quesIndex} title={`${quesItem?.label}: ${quesItem.options_value}`} placement="top-start">
                                          <span key={quesIndex} className="orange-priority-span">
                                            {quesItem?.label}: {quesItem.options_value.substring(0, 20)}...
                                          </span>
                                        </Tooltip>
                                      ) : (
                                        <span key={quesIndex} className="orange-priority-span">
                                          {quesItem?.label}: {quesItem.options_value}
                                        </span>
                                      )
                                    ) : quesItem?.options_value && quesItem.options_value.length > 20 ? (
                                      <Tooltip key={quesIndex} title={`${quesItem?.label}: ${quesItem.options_value}`} placement="top-start">
                                        <span key={quesIndex}>
                                          {quesItem?.label}: {quesItem.options_value.substring(0, 20)}...
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <span key={quesIndex}>
                                        {quesItem?.label}: {quesItem.options_value}
                                      </span>
                                    )
                                  )}
                                </div>
                                {/* <div className="left-time-slots-sec" ref={dropdownRef}> */}
                                <div className="left-time-slots-sec">
                                  {(subSlot.type === "cabinet" || subSlot.type === "video") && subSlot?.patient_details?.[0]?.id ? (
                                    <div
                                      className="patient-avatar"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (userData?.users?.type === "Doctor") {
                                          props?.navigate(`/doctor/patient-dictionary/${subSlot?.patient_details[0]?.id}`);
                                        } else if (userData?.users?.type === "Nurse") {
                                          props?.navigate(`/nurse/patient-dictionary/${subSlot?.patient_details[0]?.id}`);
                                        }
                                      }}
                                      title="View Patient Profile"
                                    >
                                      {subSlot?.patient_details[0]?.first_name?.[0]}{subSlot?.patient_details[0]?.last_name?.[0]}
                                    </div>
                                  ) : null}

                                  {subSlot?.confirm === 1 && subSlot?.appointmentStatus !== 3 ? <span className="confirmTag">Confirmed</span> : null}
                                  {subSlot?.appointmentStatus === 3 ? <span className="confirmTag absentText">Absent</span> : null}
                                  {subSlot?.appointmentStatus === 2 ? <span className="confirmTag cancelText">Cancelled</span> : null}
                                  {subSlot.type ? (
                                    <>
                                      {subSlot?.appointmentStatus !== 3 && subSlot?.appointmentStatus !== 2 ? (
                                        <>
                                          {!isPreviousDateFromCurrent(props?.currentDate) ? (
                                            <span
                                              className={`mainSelectSpan ${subSlot.finish === 1 ? "green-span" : subSlot.finish === 2 ? "blue-span" : "gray-span"
                                                } ${isPreviousDateFromCurrent(props?.currentDate) ? "disabled" : ""}`}
                                              onClick={e => {
                                                e.stopPropagation();
                                                handleToggleSelect(index + subIndex);
                                              }}></span>
                                          ) : (
                                            <span
                                              className={`mainSelectSpan ${subSlot.finish === 1 ? "green-span" : subSlot.finish === 2 ? "blue-span" : "gray-span"
                                                } ${isPreviousDateFromCurrent(props?.currentDate) ? "disabled" : ""}`}></span>
                                          )}

                                          {openIndex === subIndex + index && (
                                            <div className="selectSpanWrapper">
                                              <span
                                                className="gray-span green-span"
                                                onClick={event => {
                                                  event.stopPropagation();
                                                  props?.handleAppIsFinished(subSlot?.id, 1);
                                                  setOpenIndex(null);
                                                }}></span>
                                              <span
                                                className="gray-span blue-span"
                                                onClick={event => {
                                                  event.stopPropagation();
                                                  props?.handleAppIsFinished(subSlot?.id, 2);
                                                  setOpenIndex(null);
                                                }}></span>
                                            </div>
                                          )}
                                        </>
                                      ) : null}

                                      {(subSlot?.appointmentStatus === 0 || subSlot?.appointmentStatus === 1 || subSlot?.appointmentStatus === 4) &&
                                        subSlot.finish === 0 &&
                                        !isPreviousDateFromCurrent(props?.currentDate) ? (
                                        <div
                                          className="StylesCheckbox"
                                          onClick={() =>
                                            props?.selectedCheckboxes.has(parseInt(subSlot.id))
                                              ? props?.handleCheckboxChange({ id: parseInt(subSlot?.id), target: { checked: false } })
                                              : props?.handleCheckboxChange({ id: parseInt(subSlot?.id), target: { checked: true } })
                                          }>
                                          <input
                                            type="checkbox"
                                            value={parseInt(subSlot.id)}
                                            checked={props?.selectedCheckboxes.has(parseInt(subSlot.id))}
                                            onClick={event => event.stopPropagation()}
                                            onChange={() => {
                                              const slotId = parseInt(subSlot.id);
                                              props?.handleCheckboxChange({
                                                id: slotId,
                                                target: { checked: !props?.selectedCheckboxes.has(slotId) },
                                              });
                                            }}
                                          />
                                          <div className="checkmark"></div>
                                        </div>
                                      ) : null}
                                      {userData?.users?.type === "Doctor" ? (
                                        <>
                                          <button
                                            className="infoBtn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenAppointmentModal(subSlot);
                                            }}
                                            title="View Appointment Details">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <circle cx="12" cy="12" r="10"></circle>
                                              <line x1="12" y1="16" x2="12" y2="12"></line>
                                              <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                            </svg>
                                          </button>
                                          <button
                                            className="cancelBtn"
                                            onClick={() => {
                                              if (props?.handleCancelBtnClick) {
                                                props?.handleCancelBtnClick(subSlot?.id);
                                              }
                                            }}>
                                            Cancel
                                          </button>
                                          {/* <button
                                          className="resceheduleBtn"
                                          onClick={() => {
                                            if (props?.handleCancelBtnClick) {
                                              props?.handleCancelBtnClick(subSlot?.id);
                                            }
                                          }}>
                                          Reschedule
                                        </button> */}
                                          <button
                                            className="resceheduleBtn"
                                            onClick={() => {
                                              if (props?.handleRescheduleBtnClick) {
                                                props?.handleRescheduleBtnClick(subSlot?.id, subSlot?.doctor_id, subSlot?.consultation_type);
                                              }
                                            }}>
                                            Rescehedule
                                          </button>
                                        </>
                                      ) : null}
                                      {userData?.users?.type === "Nurse" ? (
                                        <>
                                          {/* {subSlot?.appointmentStatus !== 6 ? (
                                          <button
                                            className="resceheduleBtn"
                                            onClick={() => {
                                              if (props?.handleRescheduleBtnClick) {
                                                props?.handleRescheduleBtnClick(subSlot?.id, subSlot?.doctor_id, subSlot?.consultation_type);
                                              }
                                            }}>
                                            Rescehedule
                                          </button>
                                        ) : null} */}
                                          <button
                                            className="infoBtn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenAppointmentModal(subSlot);
                                            }}
                                            title="View Payment Details">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <circle cx="12" cy="12" r="10"></circle>
                                              <line x1="12" y1="16" x2="12" y2="12"></line>
                                              <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                            </svg>
                                          </button>
                                          <button
                                            className="cancelBtn"
                                            onClick={() => {
                                              if (props?.handleCancelBtnClick) {
                                                props?.handleCancelBtnClick(subSlot?.id);
                                              }
                                            }}>
                                            Cancel
                                          </button>
                                          <button
                                            className="resceheduleBtn"
                                            onClick={() => {
                                              if (props?.handleRescheduleBtnClick) {
                                                props?.handleRescheduleBtnClick(subSlot?.id, subSlot?.doctor_id, subSlot?.consultation_type);
                                              }
                                            }}>
                                            Rescehedule
                                          </button>
                                        </>
                                      ) : null}
                                    </>
                                  ) : null}
                                </div>
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="right-time-slots right-time-slots-blanks">&nbsp;</div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {addAppPopupOpen && <AddAppointmentForm
          isOpen={addAppPopupOpen}
          onClose={() => setAddAppPopupOpen(false)}
          initialData={addAppInitialData}
          onSuccess={handleAppointmentFormSuccess}
        />}

        <AppointmentDetailsModal
          isOpen={showAppointmentModal}
          onClose={handleCloseAppointmentModal}
          appointment={selectedAppointment}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          userType={userData?.users?.type}
        />
      </div>
    </>
  );
};

export default StaticDayView;
